import { all, call, delay, fork, put, takeEvery } from "redux-saga/effects";
import { LOGIN_USER, REGISTER_USER, LOGOUT_USER } from "../actions";
import {
  loginUserSuccess,
  loginUserError,
  registerUserSuccess,
  registerUserError,
} from "./actions";
import axios from "../../helpers/Axios";
import setAuthHeaders from "../../helpers/SetAuthHeaders";
import { post } from "axios";
import { apiPath } from "../../constants/defaultValues";
import { processors, reclassify } from "../../assets/data/reprocessors";
import { getCompanySecNo, getCompassAccess } from "../../helpers/compass";
import { setEsecureIdentity } from "../../helpers/filerCompanies";

const loginWithEmailPasswordAsync = async (email, password) =>
  post(`${apiPath}/authentication/ost/token/`, {
    username: email,
    password: password,
  })
    .then((authUser) => authUser)
    .catch((error) => error);

function* loginWithEmailPassword({ payload }) {
  const { email, password } = payload.user;
  const { history } = payload;

  try {
    const loginUser = yield call(loginWithEmailPasswordAsync, email, password);

    if (!loginUser.message) {
      const userData = loginUser.data;

      setAuthHeaders({
        authToken: userData.key,
        userID: userData.user_id,
        roleID: userData.role_id,
        appType: "eFAST",
      });

      // A ComPass company officer files as a Filer in eFAST, whatever their
      // eFAST role says. Skip accounts that are already Filers, and internal
      // roles, which have no company to look up.
      let roleName = userData.role_name;

      if (userData.company_id && roleName !== "Filer") {
        const secNo =
          userData.sec_no ||
          (yield call(getCompanySecNo, userData.company_id));
        const compassAccess = yield call(getCompassAccess, {
          secNo,
          emails: userData.email,
        });

        if (compassAccess.isFiler) {
          roleName = "Filer";
        }
      }

      localStorage.setItem("user_id", userData.user_id);
      localStorage.setItem("user_role", roleName);
      localStorage.setItem("user_role_id", userData.role_id);
      localStorage.setItem("user_username", userData.username);
      localStorage.setItem("user_first_name", userData.first_name);
      localStorage.setItem("user_last_name", userData.last_name);
      localStorage.setItem("user_email", userData.email);
      localStorage.setItem("user_token", userData.key);
      localStorage.setItem("user_status", userData.status);
      // The eSECURE identity is how get_filer_companies finds the companies of
      // an account that is not an enrolled filer.
      setEsecureIdentity({
        esecureId: userData.esecure_id || userData.user__esecure_id,
        keycloakId: userData.keycloak_id,
      });

      let stopProcessing = false;
      processors.forEach((abc) => {
        if (!stopProcessing) {
          if (abc.id === userData.user_id) {
            localStorage.setItem("reprocess", false);
            stopProcessing = true;
          } else {
            localStorage.setItem("reprocess", true);
          }
        }
      });

      let stopReclassify = false;
      reclassify.forEach((def) => {
        if (!stopReclassify) {
          if (def.id === userData.user_id) {
            localStorage.setItem("reclassify", false);
            stopReclassify = true;
          } else {
            localStorage.setItem("reclassify", true);
          }
        }
      });

      if (roleName === "Filer") {
        localStorage.setItem("user_company", userData.company_id);
        localStorage.setItem("user_company_name", userData.company_name);
        localStorage.setItem("user_company_can_upload", userData.can_upload);
        // Company-only keys must not survive a ComPass promotion.
        [
          "is_exceptional",
          "is_disputed",
          "source",
          "prompt_change",
          "company_user_group",
        ].forEach((key) => localStorage.removeItem(key));
      } else if (roleName === "Company") {
        localStorage.setItem("user_company", userData.company_id);
        localStorage.setItem("user_company_name", userData.company_name);
        localStorage.setItem("is_exceptional", userData.exceptional);
        localStorage.setItem("is_disputed", userData.disputed);
        localStorage.setItem("source", userData.source);
        localStorage.setItem("prompt_change", userData.prompt_change);
        localStorage.setItem(
          "company_user_group",
          userData.company_user_group_id
        );
      }

      yield put(loginUserSuccess({ ...userData, role_name: roleName }));
      history.push("/");
    } else {
      if (loginUser.message === "Request failed with status code 400") {
        const message = loginUser.response.data;

        if (message.message) {
          yield put(loginUserError(message.message));
          localStorage.setItem("validation", message.error_description);
        } else {
          yield put(loginUserError("Failed to log in."));
        }
      } else {
        yield put(loginUserError("Failed to log in."));
      }
    }
  } catch (error) {
    yield put(loginUserError(error));
  }
}


function* registerWithEmailPassword({ payload }) {
  const { email, password } = payload.user;
  const { history } = payload;
  try {
    const registerUser = yield call(email, password);
    if (!registerUser.message) {
      localStorage.setItem("user_id", registerUser.user.uid);
      yield put(registerUserSuccess(registerUser));
      history.push("/");
    } else {
      yield put(registerUserError(registerUser.message));
    }
  } catch (error) {
    yield put(registerUserError(error));
  }
}

const logoutAsync = async (history) => {
  const token = localStorage.getItem("user_token");
  post(`${apiPath}/authentication/token/revoke/`, {
    token: token,
  })
    .then((authUser) => authUser)
    .catch((error) => error);
  history.push("/");
};

function* logout({ payload }) {
  const { history } = payload;
  try {
    yield call(logoutAsync, history);
    localStorage.clear();
  } catch (error) { }
}

export function* watchRegisterUser() {
  yield takeEvery(REGISTER_USER, registerWithEmailPassword);
}

export function* watchLoginUser() {
  yield takeEvery(LOGIN_USER, loginWithEmailPassword);
}

export function* watchLogoutUser() {
  yield takeEvery(LOGOUT_USER, logout);
}

export default function* rootSaga() {
  yield all([fork(watchLoginUser), fork(watchLogoutUser)]);
}
